import React, { useCallback, useState } from 'react';

import {
  faChevronDown,
  faChevronRight,
  faSort,
  faSortDown,
  faSortUp,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';

export const TABLE_TITLE_CLASSES =
  'vai-flex vai-items-center vai-gap-2 vai-text-sm vai-font-medium vai-text-muted-foreground';

export interface TableColumn<T> {
  key: string;
  header: React.ReactNode;
  width?: string;
  isSortable?: boolean;
  render: (item: T, index: number) => React.ReactNode;
}

export interface SortableTableProps<T> {
  dataTestId?: string;
  data: T[];
  columns: TableColumn<T>[];
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  className?: string;
  isSorting?: boolean;
  rowKey?: (item: T, index: number) => string;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  scrollRef?: React.Ref<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
  nextAppointmentRef?: React.Ref<HTMLTableRowElement>;
  /** Render function for expandable row content. If provided, rows become expandable. */
  expandableContent?: (item: T, index: number) => React.ReactNode;
  /** Optional indicator rendered next to the expand chevron (e.g. a colored dot). */
  rowIndicator?: (item: T, index: number) => React.ReactNode;
  /** Optional tooltip content shown when hovering the expand control (indicator + chevron). */
  expandControlTooltip?: (item: T, index: number) => React.ReactNode;
}

/**
 * Generic sortable table component that can display any type of data
 * Includes rounded corners, white background, and border styling
 *
 * @template T - The type of data items in the table (e.g., Appointment, User, etc.)
 */
export const SortableTable = <T extends unknown>({
  dataTestId,
  data,
  columns,
  sortField,
  sortDirection,
  className,
  isSorting,
  rowKey,
  onSort,
  scrollRef,
  sentinelRef,
  nextAppointmentRef,
  expandableContent,
  rowIndicator,
  expandControlTooltip,
}: SortableTableProps<T>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleSort = (field: string) => {
    if (!onSort) return;

    const newDirection =
      sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(field, newDirection);
  };

  const isExpandable = !!expandableContent;

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'vai-rounded-lg vai-border vai-border-grey-200 vai-bg-white vai-overflow-hidden vai-flex-1',
        className,
      )}
    >
      <div
        className="vai-overflow-auto vai-h-full vai-pb-4"
        ref={scrollRef}
      >
        <Table className="vai-w-full">
          <TableHeader className="vai-sticky vai-top-0 vai-bg-white vai-z-10">
            <TableRow className="vai-rounded-t-lg">
              {isExpandable && <TableHead className="vai-w-[40px]" />}
              {columns.map((column) => (
                <TableHead
                  key={String(column.key)}
                  className={cn(
                    column.width || '',
                    column.isSortable
                      ? 'vai-cursor-pointer hover:vai-bg-gray-50 vai-transition-colors vai-group'
                      : '',
                  )}
                  onClick={() => column.isSortable && handleSort(column.key)}
                >
                  <div className={TABLE_TITLE_CLASSES}>
                    {column.header}
                    {column.isSortable &&
                      (() => {
                        const isCurrentColumn = sortField === column.key;
                        const isCurrentlySorting = isSorting && isCurrentColumn;

                        let sortIcon;
                        let sortIconClasses;

                        if (isCurrentlySorting) {
                          sortIcon = faSpinner;
                          sortIconClasses =
                            'vai-text-blue-600 vai-opacity-100 vai-animate-spin';
                        } else if (isCurrentColumn) {
                          sortIcon =
                            sortDirection === 'asc' ? faSortUp : faSortDown;
                          sortIconClasses = 'vai-text-blue-600 vai-opacity-100';
                        } else {
                          sortIcon = faSort;
                          sortIconClasses =
                            'vai-text-gray-400 vai-opacity-60 group-hover:vai-opacity-80';
                        }

                        return (
                          <FontAwesomeIcon
                            icon={sortIcon}
                            className={sortIconClasses}
                          />
                        );
                      })()}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => {
              const key = rowKey ? rowKey(item, index) : String(index);
              const isExpanded = expandedRows.has(key);
              const expandTooltipContent = expandControlTooltip?.(item, index);

              return (
                <React.Fragment key={key}>
                  <TableRow
                    ref={
                      index === data.length - 1 ? nextAppointmentRef : undefined
                    }
                    className={cn(
                      index === data.length - 1 && !isExpanded
                        ? 'vai-rounded-b-lg'
                        : '',
                      isExpandable && 'vai-cursor-pointer',
                    )}
                    onClick={
                      isExpandable
                        ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                            // Don't toggle when the click originated from an interactive element
                            const target = e.target as HTMLElement;
                            if (
                              target.closest(
                                'button, a, select, input, textarea, [role="menuitem"], [role="option"], [data-radix-collection-item]',
                              )
                            ) {
                              return;
                            }
                            toggleRow(key);
                          }
                        : undefined
                    }
                  >
                    {isExpandable && (
                      <TableCell className="vai-w-[40px] vai-px-3">
                        {expandTooltipContent ? (
                          <Tooltip delayDuration={0} eager>
                            <TooltipTrigger asChild>
                              <div className="vai-inline-flex vai-items-center vai-gap-1.5 vai-p-1 vai--m-1">
                                {rowIndicator?.(item, index)}
                                <FontAwesomeIcon
                                  icon={
                                    isExpanded ? faChevronDown : faChevronRight
                                  }
                                  className="vai-text-gray-400 vai-w-3 vai-h-3"
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {expandTooltipContent}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="vai-flex vai-items-center vai-gap-1.5">
                            {rowIndicator?.(item, index)}
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                              className="vai-text-gray-400 vai-w-3 vai-h-3"
                            />
                          </div>
                        )}
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell key={String(column.key)}>
                        {column.render(item, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isExpandable && isExpanded && (
                    <TableRow className="vai-border-l-4 vai-border-l-amber-400">
                      <TableCell
                        colSpan={columns.length + 1}
                        className="vai-p-0"
                      >
                        {expandableContent(item, index)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {/* Infinite scroll trigger */}
        {sentinelRef && <div ref={sentinelRef} className="vai-h-4" />}
      </div>
    </div>
  );
};
